/**
 * @file: usePreInspectionResults.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Overlay } from '@euler/app/flows/report/components/VehicleFacadeBirdView.shared';
import { toMediaObject } from '@euler/app/flows/report/functions/mediaInfoToMediaObject';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import {
  DefectiveLevel,
  FacadeSiteConfigurationMapByName,
  FacadeSiteConfigurations,
  MediaObject,
} from '@euler/model';
import { AbnormalLevel, SiteInspectionType } from '@euler/model/enum';
import { PreInspectionReport } from '@euler/model/report';
import { useMemo } from 'react';

export type PreInspectionResultGroup = {
  name: string;
  result: string;
  code?: string | null;
  defectiveLevel: DefectiveLevel;
  medias: MediaObject[];
};

export function usePreInspectionResults(
  report: PreInspectionReport | null | undefined,
) {
  return useMemo(() => {
    const items = (report?.items ?? []).filter(
      x => x.inspectionType === SiteInspectionType.Facade,
    );

    const groups: PreInspectionResultGroup[] = [];

    for (const item of items) {
      const name = item.siteName!;
      const defectiveLevel = getDefectiveLevel(
        item.abnormalLevel,
        item.severityLevel,
      );
      const result =
        item.abnormalLevel === AbnormalLevel.Fine
          ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            item.normalResultLabel || '记录'
          : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            item.abnormalResultLabel || '异常';
      let group = groups.find(x => x.name === name);
      if (!group) {
        group = {
          name,
          code: item.siteCode,
          result,
          medias: [],
          defectiveLevel,
        };
        groups.push(group);
      }
      if (group.defectiveLevel < defectiveLevel) {
        group.defectiveLevel = defectiveLevel;
        group.result = result;
      }
      if (item.medias.length) {
        for (const media of item.medias) {
          group.medias.push(toMediaObject(media));
        }
      }
    }

    const groupsWithIssues = groups.filter(
      x => x.defectiveLevel !== DefectiveLevel.Fine,
    );

    const recordGroups = groups.filter(
      x =>
        x.defectiveLevel === DefectiveLevel.Fine &&
        !FacadeSiteConfigurations.find(c => c.code === x.code),
    );

    const normalSites = groups
      .filter(x => x.defectiveLevel === DefectiveLevel.Fine)
      .map(x => FacadeSiteConfigurations.find(c => c.code === x.code)!)
      .filter(x => x);

    const overlays: Overlay[] = [];
    const map = FacadeSiteConfigurationMapByName;
    for (const group of groupsWithIssues) {
      if (!map.has(group.name)) continue;
      const configuration = map.get(group.name)!;
      const color =
        group.defectiveLevel === DefectiveLevel.Urgent ? '#e0463d' : '#ff8a00';
      overlays.push({
        name: configuration.name,
        imagePath: configuration.overlay,
        coords: configuration.coords,
        color,
      });
    }

    return {
      groupsWithIssues,
      recordGroups,
      normalSites,
      overlays,
    };
  }, [report?.items]);
}
